import { Injectable } from '@angular/core';
import { ParameterTypeEnum } from '../enums/parameterType.enum';
import { ValueConversionService } from './value-conversion.service';
import { ParameterUnitService } from './parameter-unit.service';
import { Units } from '../enums/units.enum';
import nameOf from '../shared/form/nameOf.helper';
import { Sample } from '../models/sample.model';
import { IhLabResult } from '../models/IhLabResult.model';

export interface UnitOption {
    value: number;
    label: string;
}

interface ColumnUnitMapping {
    columnName: string;
    parameterType: ParameterTypeEnum;
    useAgentUnits?: boolean;
}

export const ALL_UNITS_ENUM_FILTER_MAP: { value: number, name: string }[] = [
    { value: Units.UgPerM3, name: 'µg/m³' },
    { value: Units.MgPerM3, name: 'mg/m³' },
    { value: Units.Ppb, name: 'ppb' },
    { value: Units.Ppm, name: 'ppm' },
    { value: Units.Db, name: 'dB' },
    { value: Units.UgPerCm2, name: 'µg/cm²' },
    { value: Units.UgPerFt2, name: 'µg/ft²' },
    { value: Units.FPerCc, name: 'f/cc' },
    { value: Units.MgPerSample, name: 'mg/sample' },
    { value: Units.UgPerSample, name: 'µg/sample' },
    { value: Units.MPerS2, name: 'm/s²' },
    { value: Units.MPerS175, name: 'm/s^1.75' },
];

@Injectable({
    providedIn: 'root'
})
export class UnitFilterService {
    private static readonly LAB_RESULTS_PROPERTY = nameOf<Sample>(x => x.ihLabResults);

    private columnMappings: ColumnUnitMapping[] = [
        { columnName: `${UnitFilterService.LAB_RESULTS_PROPERTY}.${nameOf<IhLabResult>(x => x.twa)}`, parameterType: ParameterTypeEnum.Pm1, useAgentUnits: true },
        { columnName: `${UnitFilterService.LAB_RESULTS_PROPERTY}.${nameOf<IhLabResult>(x => x.concValue)}`, parameterType: ParameterTypeEnum.Pm1, useAgentUnits: true },
        { columnName: `${UnitFilterService.LAB_RESULTS_PROPERTY}.${nameOf<IhLabResult>(x => x.oel)}`, parameterType: ParameterTypeEnum.Pm1, useAgentUnits: true },
        { columnName: `${UnitFilterService.LAB_RESULTS_PROPERTY}.${nameOf<IhLabResult>(x => x.totalMass)}`, parameterType: ParameterTypeEnum.Pm1, useAgentUnits: true },

        { columnName: nameOf<Sample>(x => x.temperatureMinC), parameterType: ParameterTypeEnum.Temperature },
        { columnName: nameOf<Sample>(x => x.temperatureMaxC), parameterType: ParameterTypeEnum.Temperature },

        { columnName: nameOf<Sample>(x => x.samplePreCal), parameterType: ParameterTypeEnum.FlowRate },
        { columnName: nameOf<Sample>(x => x.samplePostCal), parameterType: ParameterTypeEnum.FlowRate },
        { columnName: nameOf<Sample>(x => x.sampleAverageCal), parameterType: ParameterTypeEnum.FlowRate },
    ];

    constructor(
        private valueConversionService: ValueConversionService,
        private parameterUnitService: ParameterUnitService
    ) { }

    isUnitAwareColumn(columnName: string): boolean {
        return !!this.findColumnMapping(columnName);
    }

    hasUnitOptions(columnName: string): boolean {
        const mapping = this.findColumnMapping(columnName);
        if (!mapping?.useAgentUnits) return false;
        return true;
    }

    private readonly cachedUnitOptions: UnitOption[] = ALL_UNITS_ENUM_FILTER_MAP.map(u => ({ value: u.value, label: u.name }));

    getAvailableUnits(columnName: string): UnitOption[] {
        const mapping = this.findColumnMapping(columnName);
        if (!mapping?.useAgentUnits) return [];
        return this.cachedUnitOptions;
    }

    convertFilterValueToBaseUnit(columnName: string, value: number, selectedUnit?: number): number {
        if (value == null || isNaN(value)) return value;

        const mapping = this.findColumnMapping(columnName);
        if (!mapping) return value;

        if (mapping.useAgentUnits && selectedUnit != null) {
            return this.valueConversionService.convertAgentUnitValueToBase(value, selectedUnit);
        }

        if (mapping.useAgentUnits) return value;

        return this.valueConversionService.convertUserPreferredValueToBase(value, mapping.parameterType);
    }

    convertFilterValueFromBaseUnit(columnName: string, value: number, selectedUnit?: number): number {
        if (value == null || isNaN(value)) return value;

        const mapping = this.findColumnMapping(columnName);
        if (!mapping) return value;

        if (mapping.useAgentUnits && selectedUnit != null) {
            return this.valueConversionService.getParameterValueAsPerAgentUnit(value, selectedUnit);
        }

        if (mapping.useAgentUnits) return value;

        return this.valueConversionService.getParameterValueFromUserPreferences(value, mapping.parameterType);
    }

    getColumnUnitLabel(columnName: string, selectedUnit?: number): string {
        const mapping = this.findColumnMapping(columnName);
        if (!mapping) return '';

        if (mapping.useAgentUnits) {
            if (selectedUnit != null) {
                return this.valueConversionService.getParameterUnitSymbol(selectedUnit) ?? '';
            }
            return '';
        }

        return this.parameterUnitService.getParameterUnit(mapping.parameterType) ?? '';
    }

    getLinkedFieldInfo(columnName: string): { linkedField: string } | null {
        const mapping = this.findColumnMapping(columnName);
        if (!mapping?.useAgentUnits) return null;

        const parts = columnName.split('.');
        if (parts.length < 2) return null;

        const parentPath = parts.slice(0, parts.length - 1).join('.');
        return { linkedField: `${parentPath}.${nameOf<IhLabResult>(x => x.agentUnits)}` };
    }

    private findColumnMapping(columnName: string): ColumnUnitMapping | undefined {
        const lowerColumnName = columnName.toLowerCase();

        return this.columnMappings.find(
            m => m.columnName.toLowerCase() === lowerColumnName
        );
    }

}
